import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { pointCalc, deviationLabel, describeDirection, fmtAbs } from "../../lib/calc";
import { LADOS } from "../../lib/constants";
import { newPoint } from "../../lib/model";
import { Field, TextInput, NumInput, NSelect, ActionButton } from "../common/Field";

export function TraceCoordinateDialog({ open, onOpenChange, insp, update }) {
  const [p, setP] = useState(() => newPoint(insp.points));
  const reset = () => setP(newPoint(insp.points));
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setC = (grp, k, v) => setP((prev) => ({ ...prev, [grp]: { ...prev[grp], [k]: v } }));
  const c = pointCalc(p, insp.reference || {});
  const label = deviationLabel(p.numero, c);

  const submit = () => {
    if ((insp.points || []).length >= 100) { toast.error("Limite de 100 pontos atingido"); return; }
    if (c.governing === null) { toast.error("Informe ao menos um par de coordenadas (referência e encontrada)"); return; }
    update("points", (list) => [...(list || []), p]);
    toast.success(`Coordenada ${p.numero} traçada no mapa`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl bg-[#0B1520] border-[#1E2F45]" data-testid="trace-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider text-2xl">Traçar coordenada</DialogTitle>
          <DialogDescription>Referência original → coordenada medida → cálculo do desvio → representação gráfica.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Ponto"><TextInput testId="trace-numero-input" value={p.numero} onChange={(v) => set("numero", v)} className="font-mono font-bold text-[#FFD500]" /></Field>
          <Field label="Descrição"><TextInput testId="trace-descricao-input" value={p.descricao} onChange={(v) => set("descricao", v)} /></Field>
          <Field label="Lado"><NSelect testId="trace-lado-select" value={p.lado} onChange={(v) => set("lado", v)} options={LADOS} placeholder="—" /></Field>
        </div>
        {[["ref", "Referência (mm)", "text-[#7FB6EE]"], ["found", "Medido (mm)", "text-white"]].map(([grp, title, cls]) => (
          <div key={grp}>
            <div className={`caption-label mb-2 ${cls}`}>{title}</div>
            <div className="grid grid-cols-3 gap-3">
              {["x", "y", "z"].map((k) => <Field key={k} label={k.toUpperCase()}><NumInput testId={`trace-${grp}-${k}-input`} value={p[grp][k]} onChange={(v) => setC(grp, k, v)} /></Field>)}
            </div>
          </div>
        ))}
        <Field label="Tolerância (± mm) — opcional"><NumInput testId="trace-tolerancia-input" value={p.tolerancia} onChange={(v) => set("tolerancia", v)} placeholder="Usa a tolerância padrão" /></Field>
        <div className="border border-[#FF0000]/40 bg-[#FF0000]/[0.06] rounded-sm p-3 font-mono text-sm" data-testid="trace-preview">
          <div className="text-[#FF0000] font-bold">{label.text} <span className="text-xs font-normal">{label.axes && `(${label.axes})`}</span></div>
          <div className="text-slate-300 text-xs mt-1">{describeDirection(c.dx, c.dy, c.dz, insp.reference || {}) || "Informe as coordenadas para calcular o desvio."}</div>
          <div className="text-slate-500 text-xs mt-1">Resultante: {fmtAbs(c.resultant)} · {c.situacao}</div>
        </div>
        <div className="flex justify-end gap-2">
          <ActionButton variant="outline" onClick={() => onOpenChange(false)} testId="trace-cancel">Cancelar</ActionButton>
          <ActionButton onClick={submit} testId="trace-submit">Traçar no mapa</ActionButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
